import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../axios/api.js";

// ─── Header details (client / contact info shown at the top of the page) ──
export const fetchInquiryDetails = createAsyncThunk(
  "inquiry/fetchInquiryDetails",
  async (rfq_no, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/inquiry-details", {
        params: { rfq_no },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue("Error fetching contact details");
    }
  }
);

// ─── Row data load (InquiryReceived.jsx — chunked, status === "Saved") ────
export const fetchSavedInquiryChunk = createAsyncThunk(
  "inquiry/fetchSavedInquiryChunk",
  async ({ rfq_no, limit, offset }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/rfqsave-fetch-data", {
        params: { rfq_no, limit, offset },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue("Error fetching data chunk");
    }
  }
);

// ─── Row data load (InquiryCompleted.jsx — already-submitted inquiry) ─────
export const fetchSubmittedInquiryData = createAsyncThunk(
  "inquiry/fetchSubmittedInquiryData",
  async (rfq_no, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/rfqsubmit-fetch-data", {
        params: { rfq_no },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue("Error fetching submitted data");
    }
  }
);

// ─── Product / size dropdown source lists ──────────────────────────────────
export const fetchMaterialCodeProducts = createAsyncThunk(
  "inquiry/fetchMaterialCodeProducts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/material-code/material_code_product");
      return response.data;
    } catch (error) {
      return rejectWithValue("Error fetching products");
    }
  }
);

export const fetchMaterialSizes = createAsyncThunk(
  "inquiry/fetchMaterialSizes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/material-code/material_size");
      return response.data;
    } catch (error) {
      return rejectWithValue("Error fetching sizes");
    }
  }
);

// ─── Per-row dependent dropdown options ────────────────────────────────────
export const fetchRowCategory = createAsyncThunk(
  "inquiry/fetchRowCategory",
  async (product, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/material-code/material_code_category", { product });
      return response.data || [];
    } catch (error) {
      return rejectWithValue("Error fetching categories");
    }
  }
);

export const fetchRowClasses = createAsyncThunk(
  "inquiry/fetchRowClasses",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/material-code/material_code_classes");
      return response.data || [];
    } catch (error) {
      return rejectWithValue("Error fetching classes");
    }
  }
);

export const fetchRowMaterial = createAsyncThunk(
  "inquiry/fetchRowMaterial",
  async (product, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/material-code/material_code_material", {
        params: { product },
      });
      return response.data || [];
    } catch (error) {
      return rejectWithValue("Error fetching materials");
    }
  }
);

export const fetchRowGrade = createAsyncThunk(
  "inquiry/fetchRowGrade",
  async ({ product, category, material }, { rejectWithValue }) => {
    try {
      const params = category ? { product, category, material } : { product, material };
      const response = await axiosClient.post("/api/material-code/material_code_grade", params);
      return response.data || [];
    } catch (error) {
      return rejectWithValue("Error fetching grades");
    }
  }
);

// InquiryReceived.jsx shape: { size }
export const fetchRowSchedule = createAsyncThunk(
  "inquiry/fetchRowSchedule",
  async (size, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/material-code/material_schedule", { size });
      return response.data || [];
    } catch (error) {
      return rejectWithValue("Error fetching schedules");
    }
  }
);

// InquiryCompleted.jsx shape: raw query + params
export const fetchRowScheduleByQuery = createAsyncThunk(
  "inquiry/fetchRowScheduleByQuery",
  async (size, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/material-code/material_schedule", {
        size
      });
      return response.data || [];
    } catch (error) {
      return rejectWithValue("Error fetching schedules");
    }
  }
);

export const fetchRowUOM = createAsyncThunk(
  "inquiry/fetchRowUOM",
  async (product, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/material-code/material_uom", { product });
      return response.data || [];
    } catch (error) {
      return rejectWithValue("Error fetching UOMs");
    }
  }
);

export const fetchRowWeight = createAsyncThunk(
  "inquiry/fetchRowWeight",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/material-code/material_code_weight", params);
      return response.data ?? "";
    } catch (error) {
      return rejectWithValue("Error fetching weight");
    }
  }
);

// General material-code lookup, shared by all product types
export const fetchRowMaterialCode = createAsyncThunk(
  "inquiry/fetchRowMaterialCode",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/material-code/material_code", { params });
      return response.data || [];
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Fallback material-code lookup for custom (non-default) products — InquiryReceived.jsx only
export const fetchProductMaterialCode = createAsyncThunk(
  "inquiry/fetchProductMaterialCode",
  async (product, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/material-code/product", { params: { product } });
      return response.data?.material_code || "";
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const generateMaterialCode = createAsyncThunk(
  "inquiry/generateMaterialCode",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/generatematerialcodetubeplate", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue("Material Code was not generated");
    }
  }
);

// ─── Row CRUD ───────────────────────────────────────────────────────────────
export const deleteInquiryRow = createAsyncThunk(
  "inquiry/deleteInquiryRow",
  async ({ rfq_no, sr_no, id }, { rejectWithValue }) => {
    try {
      await axiosClient.delete("/api/delete-inquiry-row", { data: { rfq_no, sr_no, id } });
      return sr_no;
    } catch (error) {
      return rejectWithValue("Remove row failed");
    }
  }
);

export const uploadRowImage = createAsyncThunk(
  "inquiry/uploadRowImage",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/upload-row-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue("Failed to upload image.");
    }
  }
);

// ─── File upload (bulk inquiry-details upload) ─────────────────────────────
export const uploadInquiryFile = createAsyncThunk(
  "inquiry/uploadInquiryFile",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/upload-inquiry-details", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.status !== 201) {
        return rejectWithValue("File upload error");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue("File upload error");
    }
  }
);

export const fetchClientDocUrl = createAsyncThunk(
  "inquiry/fetchClientDocUrl",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/fetch-clients-documentformat-inquiry");
      return response.data;
    } catch (error) {
      return rejectWithValue("Fetch error");
    }
  }
);

// ─── Save / Submit ──────────────────────────────────────────────────────────
export const saveInquiry = createAsyncThunk(
  "inquiry/saveInquiry",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put("/api/save-inquiry", payload);
      if (response.status !== 200) {
        return rejectWithValue("Failed to save data. Please try again.");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue("Failed to save data. Please try again.");
    }
  }
);

// InquiryReceived.jsx only — dollar rate must be set before an inquiry can be submitted
export const fetchCurrencyExchangeRate = createAsyncThunk(
  "inquiry/fetchCurrencyExchangeRate",
  async (rfq_no, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/currencyExchange/${rfq_no}`);
      return response?.data?.[0]?.dollar_rate;
    } catch (error) {
      return rejectWithValue("Please request EA to update dollar value!");
    }
  }
);

export const submitInquiry = createAsyncThunk(
  "inquiry/submitInquiry",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post("/api/submit-inquiry", payload);
      if (response.status !== 200) {
        return rejectWithValue("Failed to submit data");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue("Failed to submit data");
    }
  }
);

const inquirySlice = createSlice({
  name: "inquiry",
  initialState: {
    detailsLoading: false,
    chunkLoading: false,
    saving: false,
    submitting: false,
    uploading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchInquiryDetails
      .addCase(fetchInquiryDetails.pending, (state) => {
        state.detailsLoading = true;
        state.error = null;
      })
      .addCase(fetchInquiryDetails.fulfilled, (state) => {
        state.detailsLoading = false;
      })
      .addCase(fetchInquiryDetails.rejected, (state, action) => {
        state.detailsLoading = false;
        state.error = action.payload;
      })

      // fetchSavedInquiryChunk / fetchSubmittedInquiryData
      .addCase(fetchSavedInquiryChunk.pending, (state) => {
        state.chunkLoading = true;
      })
      .addCase(fetchSavedInquiryChunk.fulfilled, (state) => {
        state.chunkLoading = false;
      })
      .addCase(fetchSavedInquiryChunk.rejected, (state, action) => {
        state.chunkLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchSubmittedInquiryData.rejected, (state, action) => {
        state.error = action.payload;
      })

      // uploadInquiryFile
      .addCase(uploadInquiryFile.pending, (state) => {
        state.uploading = true;
      })
      .addCase(uploadInquiryFile.fulfilled, (state) => {
        state.uploading = false;
      })
      .addCase(uploadInquiryFile.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload;
      })

      // saveInquiry
      .addCase(saveInquiry.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveInquiry.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(saveInquiry.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // submitInquiry
      .addCase(submitInquiry.pending, (state) => {
        state.submitting = true;
      })
      .addCase(submitInquiry.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(submitInquiry.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      });
  },
});

export default inquirySlice.reducer;
